import { useCallback, useMemo } from "react";
import { skipToken } from "@reduxjs/toolkit/query/react";

import { useGetNearestSupplierKmQuery } from "./homeApi";
import useSupplierFeed from "./useSupplierFeed";
import useVolumeOffers from "./useVolumeOffers";
import {
  MAX_RADIUS_KM,
  hasProductFilters as hasProductFiltersFor,
  quantiseCoordinates,
} from "../search/supplierFilters";

const useHomeData = ({ coordinates, filters }) => {
  const feed = useSupplierFeed({ coordinates, filters });

  const { isSuccess, isFetching, suppliers, total } = feed;

  const feedSupplierIds = useMemo(() => {
    if (!isSuccess || isFetching) return null;
    if (total > suppliers.length) return null;

    return new Set(suppliers.map((supplier) => supplier.id));
  }, [isSuccess, isFetching, total, suppliers]);

  const volumes = useVolumeOffers({
    coordinates,
    radiusKm: filters.radiusKm,
    containerOptionId: filters.containerOptionId,
    maxVolume: filters.maxVolume,
    supplierCount: suppliers.length,
    feedSupplierIds,
    feedIsEmpty: feed.isEmpty,
  });

  const cell = useMemo(() => quantiseCoordinates(coordinates), [coordinates]);
  const hasProductFilters = hasProductFiltersFor(filters);
  const needsNearest = feed.isEmpty && !hasProductFilters && Boolean(cell);

  const nearest = useGetNearestSupplierKmQuery(needsNearest ? cell : skipToken);

  const nearestKm = Number.isFinite(nearest.currentData)
    ? nearest.currentData
    : null;

  const nearestChecked = nearest.isSuccess || nearest.isError;

  const suggestedRadiusKm =
    nearestKm === null ? null : Math.max(Math.ceil(nearestKm), 1);

  const canExpandRadius =
    suggestedRadiusKm !== null &&
    suggestedRadiusKm > filters.radiusKm &&
    suggestedRadiusKm <= MAX_RADIUS_KM;

  const emptyState = useMemo(() => {
    if (!feed.isEmpty) return null;
    if (hasProductFilters) return "filters";
    if (!nearestChecked) return "checking";
    if (canExpandRadius) return "radius";

    return "area";
  }, [feed.isEmpty, hasProductFilters, nearestChecked, canExpandRadius]);

  const { refetch: refetchFeed } = feed;
  const { refetch: refetchOffers } = volumes;

  const refetch = useCallback(() => {
    refetchFeed();
    refetchOffers();
  }, [refetchFeed, refetchOffers]);

  return {
    ...feed,
    refetch,
    cylinderCards: volumes.cards,
    cylindersTruncated: volumes.truncated,
    cylindersFetching: volumes.isFetching,
    nearestKm,
    suggestedRadiusKm,
    canExpandRadius,
    emptyState,
  };
};

export default useHomeData;
